/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
  SdfCloseButton,
  SdfIcon,
  SdfButton,
  SdfTooltip,
} from '@waypoint/react-components';
import {
  Header,
  Footer,
  LanguageDropdown,
  MainPanel,
  DynamicMessageAlert,
  AlertMessage,
} from '../../components';
import { IpmAdIframe } from '../../components/marketing-panel';
import { constants as ipmConstants } from '../../redux/ducks/ipm/ipm-constants';

import { getLocaleObjFromPropKey } from '../../util/locale';
import { getParameterByName, getReturnUrl } from '../../util/misc';
import { constants } from '../../components/login/login-comp-constants';
import { EMEA_REGION, PRODUCT_ID } from '../../util/misc/constants';
import {
  extractBrandingImageProperty,
  getOrganizationId,
  passwordRecoveryUrlCheck,
  renderSuperscriptText,
} from '../../util/misc/common';

import {
  ACCOUNTTYPE_ADP,
  LOGIN_NOT_DEFINED_ERROR,
  WELCOME_TITLE,
  ACCOUNT_LOCKED,
  ADMIN_ACCOUNT_LOCKED,
  EMPLOYEE_ACCOUNT_SUSPENDED,
  ACCOUNT_SUSPENDED,
  ERROR_TITLE,
  EMPLOYEE_ACCOUNT_INACTIVE,
  APPLICATION_ERROR_DEFAULT,
} from '../layout-constants';
import { getDefaultErrorMessage } from '../../redux/helpers/epics/error';

const { CALLING_APP_ID_BROWSER_LABEL } = constants;

const isBlank = (str) => (str || '').trim().length === 0;

function LoginLayout(props) {
  const {
    identityProviders,
    title,
    titleStyle,
    titleLockIcon,
    redirectHomeScreen,
    errorMessage,
    locale,
    mainPane,
    text,
    passwordStatus,
    passwordExpirationAt,
    isHomeScreen,
    intl,
    passwordUpdateStatus,
    changePasswordError,
    clearAlerts,
    clearPasswordAlerts,
    background,
    productLogo,
    organizationLogo,
    userid,
    passwordRecovery,
    errorCode,
    dynMsg,
    dynMsgEnabled,
    loadDynamicMessage,
    dynamicMessageLoaded,
    isAppView,
    returnToHome,
    isMobileDevice,
    titleIconName,
    iconStyle,
    ipmEnabled,
  } = props;

  // Show the right-side IPM ad panel only when ALL guards pass:
  //  1. ipmEnabled flag from redux/API response is explicitly true
  //  2. IPM URL is configured (hardcoded default constant)
  //  3. Not in app-view (embedded in native app shell)
  //  4. Not a mobile device
  //  5. Must be on the home / sign-in screen
  // CSS class `hidden lg:flex` provides an additional CSS-level guard below 1024 px.
  const ipmAdUrl = (window.env && window.env.IPM_FULLLENGTH_AD_URL)
    || ipmConstants.IPM_FULLLENGTH_AD_URL;

  // Tracks whether IpmAdIframe reported a load failure (timeout, network error,
  // HTTP 4xx/5xx, blocked URL). Once true the split layout collapses and the
  // login form expands back to full width.
  const [ipmAdFailed, setIpmAdFailed] = useState(false);

  const showIpmAd = ipmEnabled === true
    && isHomeScreen
    && !isAppView
    && !isMobileDevice
    && !!ipmAdUrl
    && !ipmAdFailed;

  // When the split layout is active, remove the <html> background image and
  // replace with #F2F2F2 so no branding graphic bleeds into the OLP panel area.
  // Restore original values on unmount or when split is not shown.
  useEffect(() => {
    if (!showIpmAd) return undefined;

    const lgQuery = window.matchMedia('(min-width: 1024px)');

    const originalBackgroundImage = document.documentElement.style.backgroundImage;
    const originalBackgroundColor = document.documentElement.style.backgroundColor;

    const applyBg = (isLg) => {
      if (isLg) {
        document.documentElement.style.backgroundImage = 'none';
        document.documentElement.style.backgroundColor = '#F2F2F2';
        document.documentElement.style.backgroundSize = '50% 100%';
        document.documentElement.style.backgroundRepeat = 'no-repeat';
        document.documentElement.style.backgroundPosition = 'left top';
      } else {
        document.documentElement.style.backgroundImage = originalBackgroundImage;
        document.documentElement.style.backgroundColor = originalBackgroundColor;
        document.documentElement.style.backgroundSize = '100% 97%';
        document.documentElement.style.backgroundRepeat = '';
        document.documentElement.style.backgroundPosition = '';
      }
    };

    applyBg(lgQuery.matches);
    const handler = (e) => applyBg(e.matches);
    lgQuery.addEventListener('change', handler);

    return () => {
      // Restore original background when IPM panel unmounts
      document.documentElement.style.backgroundImage = originalBackgroundImage;
      document.documentElement.style.backgroundColor = originalBackgroundColor;
      document.documentElement.style.backgroundSize = '100% 97%';
      document.documentElement.style.backgroundRepeat = '';
      document.documentElement.style.backgroundPosition = '';
      lgQuery.removeEventListener('change', handler);
    };
  }, [showIpmAd]); // eslint-disable-line react-hooks/exhaustive-deps

  let disableDynamicMsgKillSwitch = false;

  if (
    window.env
    && (window.env.DISABLE_DYNAMIC_MESSAGE === 'true'
      || window.env.DISABLE_DYNAMIC_MESSAGE === true)
  ) {
    disableDynamicMsgKillSwitch = true;
  }
  useEffect(() => {
    if (
      !disableDynamicMsgKillSwitch
      && dynMsgEnabled
      && isHomeScreen
      && !dynamicMessageLoaded
    ) {
      loadDynamicMessage();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { id } = title.props;
  const { i18n } = text;
  const accountTypeADP = identityProviders
    && identityProviders.find((item) => item.type === ACCOUNTTYPE_ADP);
  const isAccountTypeADP = accountTypeADP && accountTypeADP.type === ACCOUNTTYPE_ADP;
  const clearAlertswithDefaults = clearAlerts || false;
  const clearPasswordAlertswithDefaults = clearPasswordAlerts || false;

  const populateAlertCode = () => {
    if (!isBlank(errorMessage)) {
      return errorMessage;
    }
    if (!isBlank(changePasswordError)) {
      return changePasswordError;
    }
    if (
      !isBlank(passwordStatus)
      && passwordStatus !== 'RESET'
      && !clearPasswordAlertswithDefaults
    ) {
      return passwordStatus;
    }
    if (
      !isBlank(passwordUpdateStatus)
      && !clearAlertswithDefaults
      && !clearPasswordAlertswithDefaults
    ) {
      return passwordUpdateStatus;
    }
    return '';
  };

  let params = {};

  const getLocalizedAlert = (localizedAlertCode) => {
    if (!isBlank(localizedAlertCode) && passwordStatus === 'ABOUT_TO_EXPIRE') {
      const expiryDays = parseInt(
        (new Date(passwordExpirationAt) - new Date()) / (24 * 3600 * 1000),
        10,
      );
      const expiryDaysExcludeSameday = expiryDays === 0 ? 1 : expiryDays;
      const localeData = intl.formatMessage({
        id: localizedAlertCode,
        defaultMessage: localizedAlertCode,
      });

      return localeData.replace('[X]', expiryDaysExcludeSameday);
    }
    if (localizedAlertCode === getDefaultErrorMessage) {
      return intl.formatMessage({
        id: APPLICATION_ERROR_DEFAULT,
        defaultMessage: localizedAlertCode,
      });
    }
    return intl.formatMessage({
      id: localizedAlertCode,
      defaultMessage: `${localizedAlertCode}[]`,
    });
  };

  const alertCode = populateAlertCode();
  const signInHomeTitle = getLocaleObjFromPropKey(i18n, locale)
    ? getLocaleObjFromPropKey(i18n, locale).SIGN_IN_HOME_TITLE
    : intl.formatMessage({
      id: title.props.id,
      defaultMessage: title.props.defaultMessage,
    });
  const welcomeTitle = id === WELCOME_TITLE
    ? signInHomeTitle
    : intl.formatMessage({
      id: title.props.id,
      defaultMessage: title.props.defaultMessage,
    });
  const pageTitle = id === ERROR_TITLE ? '' : welcomeTitle;

  let localizedAlertContent = !isBlank(alertCode)
    ? getLocalizedAlert(alertCode)
    : '';

  const applyAdminLockMessage = () => {
    const matchData = localizedAlertContent.match(/\[(.*?)\]/);

    const linkText = matchData[1];

    const { version } = passwordRecovery || '';

    let { url } = passwordRecovery || '';

    url = url === '' || url === undefined ? passwordRecoveryUrlCheck(url) : url;

    const returnURLFromSignInAPI = identityProviders && identityProviders.length > 0
      ? identityProviders[0].returnUrl
      : '';
    const returnURL = getReturnUrl() || returnURLFromSignInAPI;
    const callingAppID = getParameterByName(CALLING_APP_ID_BROWSER_LABEL);

    const backgroundImageFlt = extractBrandingImageProperty(background, locale);
    const productLogoImageFlt = extractBrandingImageProperty(
      productLogo,
      locale,
    );
    const organizationLogoImageFlt = extractBrandingImageProperty(
      organizationLogo,
      locale,
    );

    params = {
      callingAppId: callingAppID,
      returnUrl: returnURL,
      rtnURL: returnURL,
      userId: userid,
    };

    if (version === EMEA_REGION) {
      params = {
        ...params,
        // eslint-disable-next-line max-len
        action: url,
        locale,
        productId: getParameterByName(PRODUCT_ID) || '',
        organizationId: getOrganizationId(),
      };
    } else {
      params = {
        ...params,
        action: url,
        backgroundImageUrl: backgroundImageFlt,
        productLogoUrl: productLogoImageFlt,
        organizationLogoUrl: organizationLogoImageFlt,
      };
    }

    const FYPContent = `<button id='alertLink'>${linkText}</button>`;

    localizedAlertContent = localizedAlertContent.replace(
      `[${linkText}]`,
      FYPContent,
    );
  };

  const organizationLogoImage = extractBrandingImageProperty(
    organizationLogo,
    locale,
  );

  if (!isAppView && alertCode === ACCOUNT_LOCKED) {
    applyAdminLockMessage();
  }

  return (
    <div className="h-full flex flex-col justify-between bg-white md:bg-transparent p-0">
      <main
        role="main"
        className={ `flex-grow${showIpmAd && isHomeScreen ? ' flex flex-col' : ''}` }
      >
        { !isAppView && organizationLogoImage && <Header { ...props } /> }
        <div
          className={
            !isAppView
              ? showIpmAd && isHomeScreen
                ? 'flex flex-col flex-1'
                : `md:mt-20 md:mb-20 ${!isHomeScreen ? 'h-full md:h-auto' : ''}`
              : 'h-full'
          }
        >
          { /* Dynamic message for non-IPM flow: floats above the white card, matching its width */ }
          { !isAppView && dynMsg && !(showIpmAd && isHomeScreen) && (
            <div className="md:w-2xl md:max-w-2xl md:mx-auto mb-2">
              <DynamicMessageAlert
                dynMsg={ dynMsg }
                locale={ locale }
                isAppView={ isAppView }
              />
            </div>
          ) }
          <div
            className={
              isAppView
                ? `px-6 py-4 h-full bg-white border-0 ${
                  isHomeScreen ? 'grid items-center box-border' : ''
                }`
                : `${showIpmAd && isHomeScreen ? 'flex-1 lg:flex lg:flex-row lg:pl-8 lg:gap-8 items-stretch' : 'md:w-2xl md:max-w-2xl bg-white p-6 md:mx-auto md:shadow-24 md:rounded-2xl box-border'} ${
                  !isHomeScreen ? 'h-full md:h-auto' : ''
                }`
            }
          >
            { /* Left column: alert sits above the login card, both confined to the left side */ }
            <div
              className={
                !isAppView && showIpmAd && isHomeScreen
                  ? 'w-full lg:w-2xl lg:max-w-2xl lg:flex-none box-border flex flex-col md:my-20 md:w-2xl md:mx-auto lg:mx-0'
                  : 'w-full'
              }
            >
              { /* Dynamic message for IPM flow: above the login card, within the left 50% */ }
              { !isAppView && dynMsg && showIpmAd && isHomeScreen && (
                <div className="mb-2">
                  <DynamicMessageAlert
                    dynMsg={ dynMsg }
                    locale={ locale }
                    isAppView={ isAppView }
                  />
                </div>
              ) }
              { /* Login form card */ }
              <div
                className={
                  !isAppView && showIpmAd && isHomeScreen
                    ? 'w-full flex flex-col bg-white p-6 md:shadow-24 md:rounded-2xl box-border'
                    : 'w-full'
                }
              >
                { !isHomeScreen && (
                  <div className="flex mb-2">
                    { !titleLockIcon
                      && titleIconName
                      && !isAppView
                      && !isMobileDevice && (
                        <SdfIcon
                          icon={ titleIconName }
                          tabindex="0"
                          icon-title={ titleIconName }
                          className={ iconStyle || 'text-tertiary mr-2 text-2xl' }
                        />
                    ) }

                    { titleLockIcon && !isAppView && !isMobileDevice && (
                      <SdfIcon
                        icon="status-locked"
                        icon-title={ intl.formatMessage({
                          id: 'secure_lock_title',
                          defaultMessage: 'Secure page lock icon',
                        }) }
                        tabindex="0"
                        className="text-tertiary me-2 text-2xl"
                      />
                    ) }
                    <h1
                      id="login-layout_welcome"
                      className={
                        titleStyle
                        || 'text-heading-05 font-bold m-0 text-left pb-0'
                      }
                    >
                      { renderSuperscriptText(pageTitle) }
                    </h1>
                    { redirectHomeScreen && !isAppView && !isMobileDevice && (
                      <SdfCloseButton
                        size="sm"
                        class="hydrated items-center"
                        style={ { marginInlineStart: 'auto' } }
                        title={ intl.formatMessage({
                          id: 'signin.close',
                          defaultMessage: 'Close',
                        }) }
                        onClick={ returnToHome }
                      />
                    ) }
                  </div>
                ) }

                { !isHomeScreen && <AlertMessage { ...props } /> }
                { !isAppView && (
                  <div className="flex md:justify-between justify-end">
                    { errorCode === EMPLOYEE_ACCOUNT_SUSPENDED
                    || errorCode === ACCOUNT_SUSPENDED
                    || errorCode === EMPLOYEE_ACCOUNT_INACTIVE
                    || errorCode === ACCOUNT_LOCKED
                    || errorCode === ADMIN_ACCOUNT_LOCKED
                      ? ''
                      : isHomeScreen && (
                      <div className="self-center hidden md:block">
                        <SdfTooltip
                          className="text-disabled"
                          trigger="hover"
                          state="neutral"
                          attachment-point="bottom"
                        >
                          <SdfButton
                            id="signin.securepopTextBtnId"
                            slot="tooltip-target"
                            variant="text"
                            button-title="secure information"
                          >
                            <SdfIcon
                              className="ml-1 text-lg text-disabled"
                              icon="status-locked"
                            />
                          </SdfButton>
                          <span>
                            { intl.formatMessage({
                              id: 'signin.securelock',
                              defaultMessage:
                                    'Information you share, send, or receive through the site is secure. For your security, please review the URL address of your ADP website.',
                            }) }
                          </span>
                        </SdfTooltip>
                      </div>
                      ) }
                    { isHomeScreen && <LanguageDropdown { ...props } /> }
                  </div>
                ) }
                { isAccountTypeADP ? mainPane : LOGIN_NOT_DEFINED_ERROR }
                { !isAppView && isHomeScreen && <MainPanel { ...props } /> }
              </div>
              { /* end login form card */ }
            </div>
            { /* end left column */ }
            { /* Marketing Panel: only on home screen desktop when IPM URL is configured */ }
            { showIpmAd && isHomeScreen && (
              <div
                className="hidden lg:flex lg:flex-1"
                style={ { backgroundColor: '#F2F2F2' } }
              >
                <IpmAdIframe
                  iframeId="ipmAdvertsLogin"
                  iframeName="ipmAdvertsLogin"
                  iframeTitle="ipmAdverts"
                  actionUrl={ ipmAdUrl }
                  page="login"
                  className="w-full h-full"
                  sandbox={ false }
                  onSubmitError={ (err) => {
                    // eslint-disable-next-line no-console
                    console.error('[IpmAdIframe]', err);
                    // Collapse the split layout — login form expands to full width
                    setIpmAdFailed(true);
                  } }
                />
              </div>
            ) }
          </div>
        </div>
      </main>

      <Footer { ...props } />
    </div>
  );
}

LoginLayout.propTypes = {
  children: PropTypes.element,
  mainPane: PropTypes.element.isRequired,
  formRedirectURL: PropTypes.func,
  currentlySending: PropTypes.bool,
  setBrandingStylesLoaded: PropTypes.func,
};

export default injectIntl(LoginLayout);
